import { Modal, message, notification } from 'antd';
import { get, isNumber } from 'lodash-es';
import {
  BackendAxiosError,
  CustomCancelError,
  MediaType,
  MonorepoType,
  getTransErrorMsg,
  specialInnerCodeMap,
} from '@yaotai/common-utils';
import { UN_AUTHENTICATE_CODE_LIST } from '../constants';
import { reportSentryError } from './sentry';

const IMPORT_FILE_FIELD_LABEL: Record<string, string> = {
  email: '邮箱',
  identity: '身份',
  name: '昵称',
  phone: '手机',
  sex: '性别',
};

export const CLOUD_RESOURCE_POOL_UPDATE_ERROR = 12200;
const specialCodeTipMap = {
  [specialInnerCodeMap['unNoticeError']]: (error: BackendAxiosError, msg?: string) => {
    // 不需要提示
  },
  [CLOUD_RESOURCE_POOL_UPDATE_ERROR]: (error: BackendAxiosError, msg?: string) => {
    // 不需要提示
  },
  [specialInnerCodeMap['activitiesCode']]: (error: BackendAxiosError, msg?: string) => {
    // 不需要提示
  },
  [specialInnerCodeMap['activitiesCode']]: (error: BackendAxiosError, msg?: string) => {},
  [specialInnerCodeMap['npcImportFileTemplateError']]: (error: BackendAxiosError, msg?: string) => {
    // Note： npc文案导入模版错误处理
    const content = typeof error?.response?.data.detail === 'string' ? error.response.data.detail : msg;
    Modal.error({
      title: '批量文案导入',
      content: <div>{content}</div>,
      centered: true,
    });
  },
  [specialInnerCodeMap['npcImportFileError']]: (error: BackendAxiosError, msg?: string) => {
    // Note： npc文案导入内容错误处理
    const errMsg = get(error, 'response.data.msg');

    if (errMsg) {
      message.error(errMsg);
    }
    return Promise.reject(error);
  },
  [specialInnerCodeMap['importFileFieldInvalid']]: (error: BackendAxiosError) => {
    // NOTE: v1.3.2 参会人员的批量导入单独处理，依赖业务数据
    const url = get(error, 'response.data.request_info.uri');
    if (url?.startsWith('/api/v1/players') && url?.endsWith('Import')) {
      return Promise.reject(error);
    }

    // NOTE: 以下原有逻辑影响范围未知，暂不删除
    let msg = get(error, 'response.data.msg');
    if (msg) {
      message.error(msg);
      return;
    }
    const invalidField = get(error?.response?.data.detail, '0.error.0.loc.0');
    msg =
      invalidField && IMPORT_FILE_FIELD_LABEL[invalidField]
        ? `存在未填写或不符合要求的${IMPORT_FILE_FIELD_LABEL[invalidField]}`
        : '存在未填写或不符合要求的字段';
    message.error(msg);
  },
  [specialInnerCodeMap['importFileFieldRepeat']]: (error: BackendAxiosError) => {
    const url = get(error, 'response.data.request_info.uri');
    if (url?.startsWith('/api/v1/players') && url?.endsWith('Import')) {
      return Promise.reject(error);
    }
    let msg = get(error, 'response.data.msg');
    if (msg) {
      message.error(msg);
      return;
    }
    const invalidField = get(error?.response?.data.detail, '0.error.0.loc.0');
    msg =
      invalidField && IMPORT_FILE_FIELD_LABEL[invalidField]
        ? `存在未填写或不符合要求的${IMPORT_FILE_FIELD_LABEL[invalidField]}`
        : '存在未填写或不符合要求的字段';
    message.error(msg);
  },
  [specialInnerCodeMap['importFileCountsError']]: (error: BackendAxiosError, msg: string) => {
    const url = get(error, 'response.data.request_info.uri');
    if (url === '/api/v1/players') {
      message.error(msg);
    }
    return Promise.reject(error);
  },
  [specialInnerCodeMap['updateClothError']]: (error: BackendAxiosError, msg: string) => {
    message.error(msg);
    return Promise.reject(error);
  },
  [specialInnerCodeMap['importFileHeaderInvalid']]: (error: BackendAxiosError, msg: string) => {
    const content = typeof error?.response?.data.detail === 'string' ? error.response.data.detail : msg;
    Modal.error({
      title: '文件内容错误',
      content: <div>{content}</div>,
      centered: true,
    });
  },
  [specialInnerCodeMap['registrationIsFull']]: (error: BackendAxiosError, msg: string) => {
    message.info('报名人数超过上限');
  },
  [specialInnerCodeMap['spaceTimeInvalid']]: (error: BackendAxiosError, msg: string) => {
    const detail = error?.response?.data.detail;
    if (isNumber(detail)) {
      detail > 0 && message.error(`剩余时长（${detail}分钟）已不足，请减少或联系渠道经理`);
      (detail === 0 || detail < 0) && message.error(`当前剩余时长为0，请联系渠道经理`);
    }
  },
  [specialInnerCodeMap['liveStreamPullError']]: (error: any, msg: string) => {
    return Promise.reject(error);
  },
  [specialInnerCodeMap['adoptCountOverflow']]: (error: BackendAxiosError, msg: string) => {
    return Promise.reject(error);
  },
  [specialInnerCodeMap['pullResourceError']]: (error: BackendAxiosError, msg: string) => {
    notification.error({
      message: '拉起资源失败',
      description: msg,
      duration: null,
    });
  },
  [specialInnerCodeMap['totalDurationError']]: (error: BackendAxiosError, msg: string) => {
    Modal.error({
      content: msg,
      centered: true,
    });
  },

  // center 特有的返回 true 的逻辑
  [specialInnerCodeMap['bindMobileError']]: (error: BackendAxiosError, msg: string) => {
    return true;
  },
  [specialInnerCodeMap['getCodeFrequent']]: (error: BackendAxiosError, msg: string) => {
    return true;
  },
  [specialInnerCodeMap['getCodeFrequentAndLaterTry']]: (error: BackendAxiosError, msg: string) => {
    return true;
  },
};

const ignoreCodeList = [
  specialInnerCodeMap['garbageDetection'],
  specialInnerCodeMap['jwtTokenError'],
  specialInnerCodeMap['jwtTokenTimeOut'],
  specialInnerCodeMap['facialError'],
  specialInnerCodeMap['smartUploadError'],
];

type GetErrorHandlerParams = {
  mediaType?: MediaType;
  monorepoType: MonorepoType;
  unAuthCb?: (error: BackendAxiosError, msg: string) => any;
};

// eslint-disable-next-line react-func/max-lines-per-function
export function getErrorHandler({
  unAuthCb = (error: BackendAxiosError, msg: string) => {
    window.location.href = window.origin;
  },
  monorepoType,
  mediaType = 'pc',
}: GetErrorHandlerParams) {
  // eslint-disable-next-line react-func/max-lines-per-function
  return (error: BackendAxiosError) => {
    // Ignore error when request is canceled by user:
    const isCanceled = !(error instanceof Error) && error && typeof (error as CustomCancelError).message === 'string';

    if (isCanceled) return;
    reportSentryError(error);

    // 处理前端侧「断网」报错
    if (error?.message?.indexOf('Network Error') > -1) {
      message.error('请检查网络连接情况');
      return Promise.reject(error);
    }

    // 处理前端侧「请求超时」报错
    if (error?.message?.indexOf('timeout') > -1) {
      message.error('请求超时请重试');
      return Promise.reject(error);
    }

    // 处理后端返回报错
    if (!error?.response) return;

    const m = get(error, 'response.data.msg');
    const msg = m || get(error, 'response.data.error', '服务器错误');

    const statusCode = get(error, 'response.status');
    const innerCode = get(error, 'response.data.code', 0);
    const requestUri = get(error, 'response.data.request_info.uri');

    // 判断所有需要特殊处理的错误码逻辑
    if (specialCodeTipMap[innerCode]) {
      const isResolve: any = specialCodeTipMap[innerCode]?.(error, msg);
      return isResolve === true ? Promise.resolve(get(error, 'response.data')) : Promise.reject(error);
    } else if (UN_AUTHENTICATE_CODE_LIST.includes(Number(statusCode))) {
      unAuthCb(error, msg);
      return;
    } else if (ignoreCodeList.includes(innerCode)) {
      const errCondition = [
        innerCode === specialInnerCodeMap['facialError'] && requestUri === '/api/v1/facial_preview',
        innerCode === specialInnerCodeMap['garbageDetection'] && requestUri !== '/api/v1/antispam',
        requestUri === '/api/v1/images',
      ];
      if (errCondition.some(Boolean)) {
        message.error(msg);
      }
      return Promise.reject(error);
    } else {
      message.error(getTransErrorMsg(msg));
      return Promise.reject(error);
    }
  };
}
